// src/components/common/ProjectCard/ProjectCard.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiMapPin, FiTrendingUp, FiDollarSign } from 'react-icons/fi';
import { type Project } from '../../../types/Project';
import { ENV } from '../../../config/env';
import styles from './ProjectCard.module.css';

interface ProjectCardProps {
  project: Project;
  detailDisabled?: boolean;
  disabledReason?: string;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, detailDisabled = false, disabledReason }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const isSpanish = i18n.language.startsWith('es');
  const locale = isSpanish ? 'es-MX' : 'en-US';
  const currency = isSpanish ? 'MXN' : 'USD';

  const formattedMinInvestment = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0
  }).format(project.minimumInvestment);

  const progress = Math.max(0, Math.min(100, Number(project.fundingProgress) || 0));
  const resolveImageUrl = (value?: string) => {
    if (!value) return '';
    const trimmed = String(value).trim();
    if (!trimmed) return '';
    if (trimmed === '/placeholder-project.jpg') return trimmed;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;

    const apiBase = typeof ENV.API_URL === 'string'
      ? ENV.API_URL.replace(/\/api\/?$/, '')
      : '';

    if (!apiBase) return trimmed;

    if (trimmed.startsWith('/public/')) return `${apiBase}${trimmed}`;
    if (trimmed.startsWith('/images/')) return `${apiBase}/public${trimmed}`;
    if (trimmed.startsWith('images/')) return `${apiBase}/public/${trimmed}`;
    if (trimmed.startsWith('public/')) return `${apiBase}/${trimmed}`;

    return `${apiBase}/${trimmed.replace(/^\/+/g, '')}`;
  };

  const fallbackImage = '/Profunding_FondoDegradado.png';
  const imageSrc = resolveImageUrl(project.imageUrl) || fallbackImage;

  return (
    <div className={styles.card}>
      <div className={styles.imageContainer}>
        <img
          src={imageSrc}
          alt={project.title}
          className={styles.image}
          onError={e => {
            if (e.currentTarget.dataset.fallback === 'true') return;
            e.currentTarget.dataset.fallback = 'true';
            e.currentTarget.src = fallbackImage;
          }}
        />
        {project.isFeatured ? <span className={styles.featuredTag}>{t('projects.featured_label')}</span> : null}
      </div>

      <div className={styles.content}>
        <h3 className={styles.title}>{project.title}</h3>

        <div className={styles.metaRow}>
          <span className={styles.location}>
            <FiMapPin /> {project.location}
          </span>
          <span className={styles.returnRate}>
            <FiTrendingUp /> {project.returnRate}%
          </span>
        </div>

        <div className={styles.stats}>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>{t('projects.min_investment_short')}</span>
            <span className={styles.statValue}>
              <FiDollarSign /> {formattedMinInvestment}
            </span>
          </div>

          <div className={styles.statItem}>
            <span className={styles.statLabel}>{t('projects.funded_label')}</span>
            <span className={styles.statValueAccent}>{progress}%</span>
          </div>
        </div>

        <div className={styles.progressBar} aria-label={t('projects.funded_label')}>
          <div className={styles.progress} style={{ width: `${progress}%` }} />
        </div>

        <button 
          className={detailDisabled ? `${styles.ctaButton} ${styles.ctaButtonDisabled}` : styles.ctaButton}
          type="button"
          disabled={detailDisabled}
          onClick={() => {
            if (detailDisabled) return;
            navigate(`/proyectos/${project.id}`);
          }}
        >
          {detailDisabled ? 'Vista previa pendiente' : t('projects.view_details')}
        </button>
        {detailDisabled && disabledReason ? (
          <p className={styles.disabledHint}>{disabledReason}</p>
        ) : null}
      </div>
    </div>
  );
};

export default ProjectCard;
